import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { scorecardTemplateApi, programScorecardApi, scorecardUtils } from '../../api/scorecard';
import styles from './ScorecardTemplateBrowser.module.css';

const ScorecardTemplateBrowser = ({ programId, programSport, onTemplateSelected, onCancel }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configuring, setConfiguring] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, [programSport]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get templates for this sport
      const response = await scorecardTemplateApi.getTemplatesBySport(programSport);
      
      // Also get generic template as fallback
      const genericResponse = await scorecardTemplateApi.getTemplatesBySport('Other');
      
      const allTemplates = [...response.templates, ...genericResponse.templates];
      setTemplates(allTemplates);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
  };

  const handleConfirm = async () => {
    if (!selectedTemplate) return;
    
    try {
      setConfiguring(true);
      setError(null);
      
      await programScorecardApi.configureScorecard(programId, selectedTemplate._id);
      
      onTemplateSelected(selectedTemplate);
    } catch (err) {
      console.error('Error configuring scorecard:', err);
      const errorMessage = err.message || 'Failed to configure scorecard';
      setError(`Configuration failed: ${errorMessage}. Please check the console for details.`);
    } finally {
      setConfiguring(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading templates...</p>
        </div>
      );
    }

    if (error && !templates.length) {
      return (
        <div className={styles.error}>
          <p>❌ {error}</p>
          <button onClick={loadTemplates} className={styles.retryButton}>
            Try Again
          </button>
        </div>
      );
    }

    return (
      <>
      <div className={styles.header}>
        <h2>{scorecardUtils.getSportIcon(programSport)} Select Scorecard Template</h2>
        <p>Choose a template for {programSport} events</p>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          ⚠️ {error}
        </div>
      )}

      <div className={styles.templatesGrid}>
        {templates.map(template => (
          <div
            key={template._id}
            className={`${styles.templateCard} ${selectedTemplate?._id === template._id ? styles.selected : ''}`}
            onClick={() => handleTemplateClick(template)}
          >
            <div className={styles.templateIcon}>
              {scorecardUtils.getSportIcon(template.sport)}
            </div>
            
            <h3>{template.name}</h3>
            <p className={styles.description}>{template.description}</p>
            
            <div className={styles.templateDetails}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Scoring:</span>
                <span className={styles.detailValue}>
                  {template.schema.scoring.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
              
              {template.schema.periods.enabled && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Periods:</span>
                  <span className={styles.detailValue}>
                    {template.schema.periods.count} × {template.schema.periods.names[0]}
                  </span>
                </div>
              )}
              
              {template.schema.timer.enabled && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Timer:</span>
                  <span className={styles.detailValue}>
                    {template.schema.timer.duration} min ({template.schema.timer.type})
                  </span>
                </div>
              )}
              
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Statistics:</span>
                <span className={styles.detailValue}>
                  {template.schema.statistics.length} tracked
                </span>
              </div>
            </div>

            {selectedTemplate?._id === template._id && (
              <div className={styles.selectedBadge}>
                ✓ Selected
              </div>
            )}
          </div>
        ))}
      </div>

      {templates.length === 0 && (
        <div className={styles.noTemplates}>
          <p>No templates found for {programSport}</p>
          <p className={styles.hint}>Try selecting a different sport or use the Generic template</p>
        </div>
      )}

      <div className={styles.actions}>
        <button 
          onClick={handleConfirm} 
          className={styles.confirmButton}
          disabled={!selectedTemplate || configuring}
        >
          {configuring ? 'Configuring...' : 'Configure This Template'}
        </button>
        <button 
          onClick={onCancel} 
          className={styles.cancelButton}
          disabled={configuring}
        >
          Cancel
        </button>
      </div>
      </>
    );
  };

  return createPortal(
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onCancel}>
          ✕
        </button>
        {renderContent()}
      </div>
    </div>,
    document.body
  );
};

export default ScorecardTemplateBrowser;

